import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { formatPlanPrice } from '../../utils/planPrice';
import { PLAN_GATING_ENABLED } from '../../utils/plan';

const MAX_AVATAR_BYTES = 1024 * 1024;

function SubscriptionBillingCard() {
  const { role, isPro } = useAuth();
  const { subscription, usage, limits, renewalDue, openUpgradeModal, cancelSubscription } = useSubscription();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { t } = useTranslation('subscription');

  const isSpecial = role === 'special';
  // Special-role access isn't "comped" in the admin-granted sense - it's inherent to
  // the role, so it gets its own badge/note instead of being lumped in with that copy.
  const isComped = isPro && !subscription && !isSpecial;
  // isPro is true for everyone while gating is off, so badging every account "Pro"
  // would claim a plan they never bought. Only a real subscription or the special
  // role earns the paid badge; everyone else sees the free badge and the note below.
  const showPaidBadge = PLAN_GATING_ENABLED ? isPro : (isSpecial || !!subscription);
  const showAllFreeNote = !PLAN_GATING_ENABLED && !subscription && !isSpecial;

  const handleCancel = async () => {
    const confirmed = await confirm(t('billing.cancelConfirm'));
    if (!confirmed) return;
    const result = await cancelSubscription();
    if (!result.success) {
      addToast(result.error, 'error');
      return;
    }
    addToast(t('billing.cancelSuccess'), 'success');
  };

  return (
    <div className="card form-card settings-form">
      <div className="page-header">
        <h3 style={{ margin: 0 }}>{t('billing.heading')}</h3>
        <span className={`plan-badge${isSpecial ? ' plan-special' : showPaidBadge ? ' plan-pro' : ''}`}>
          {t(isSpecial ? 'badge.special' : showPaidBadge ? 'badge.pro' : 'badge.free')}
        </span>
      </div>

      {!isPro && (
        <>
          {Object.entries(usage.budgetsByType || {}).map(([type, count]) => (
            <div className="usage-bar-row" key={type}>
              <div className="usage-bar-label">
                <span>{type}</span>
                <strong>{count} / {limits.budgetsPerType}</strong>
              </div>
              <div className="progress-bar"><div style={{ width: `${Math.min(100, (count / limits.budgetsPerType) * 100)}%` }} /></div>
            </div>
          ))}
          <div className="usage-bar-row">
            <div className="usage-bar-label">
              <span>{t('billing.usageWallets')}</span>
              <strong>{usage.walletsCount} / {limits.wallets}</strong>
            </div>
            <div className="progress-bar"><div style={{ width: `${Math.min(100, (usage.walletsCount / limits.wallets) * 100)}%` }} /></div>
          </div>
          <div className="usage-bar-row">
            <div className="usage-bar-label">
              <span>{t('billing.usageAi')}</span>
              <strong>{usage.aiCreditsUsedToday} / {limits.aiCreditsPerDay}</strong>
            </div>
            <div className="progress-bar"><div style={{ width: `${Math.min(100, (usage.aiCreditsUsedToday / limits.aiCreditsPerDay) * 100)}%` }} /></div>
          </div>
          <button type="button" className="primary-btn standout-btn" onClick={() => openUpgradeModal('default')}>
            {t('billing.upgradeButton')}
          </button>
        </>
      )}

      {showAllFreeNote && <p>{t('billing.allFreeNote')}</p>}
      {isSpecial && <p>{t('billing.specialNote')}</p>}
      {isPro && isComped && <p>{t('billing.compedNote')}</p>}

      {isPro && subscription && (
        <>
          {renewalDue && (
            <div className="upgrade-banner renewal-reminder">
              <p>{t('billing.renewalDueMessage', { date: new Date(subscription.currentPeriodEnd).toLocaleDateString() })}</p>
              <button type="button" className="primary-btn standout-btn" onClick={() => openUpgradeModal('renewal')}>
                {t('billing.renewNowButton')}
              </button>
            </div>
          )}
          <div className="billing-summary-row">
            <span>{t('billing.lockedPrice')}</span>
            <strong>{formatPlanPrice(subscription.lockedPrice, subscription.currency)}{' '}{t(`interval.${subscription.interval}Period`)}</strong>
          </div>
          <div className="billing-summary-row">
            <span>{t('billing.plan')}</span>
            <strong>{t(`interval.${subscription.interval}Label`)}</strong>
          </div>
          <div className="billing-summary-row">
            <span>{subscription.autoRenew ? t('billing.nextRenewal') : t('billing.autoRenewOff')}</span>
            <strong>{new Date(subscription.currentPeriodEnd).toLocaleDateString()}</strong>
          </div>
          {subscription.autoRenew && subscription.status === 'active' && (
            <button type="button" className="text-btn danger" onClick={handleCancel}>{t('billing.cancelButton')}</button>
          )}
        </>
      )}
    </div>
  );
}

function DangerZoneCard() {
  const { isGoogleAccount, deleteAccount } = useAuth();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { t } = useTranslation('settings');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setError('');
    if (!isGoogleAccount && !password) {
      setError(t('userSettings.deleteAccountPasswordRequired'));
      return;
    }
    const confirmed = await confirm(t('userSettings.deleteAccountConfirm'));
    if (!confirmed) return;

    setIsDeleting(true);
    const result = await deleteAccount(password);
    setIsDeleting(false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    addToast(t('userSettings.deleteAccountSuccess'), 'success');
  };

  return (
    <div className="card form-card settings-form">
      <h3 style={{ margin: 0 }}>{t('userSettings.dangerZoneHeading')}</h3>
      <p className="pricing-card-note">{t('userSettings.deleteAccountWarning')}</p>
      {!isGoogleAccount && (
        <label>
          {t('userSettings.currentPassword')}
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
        </label>
      )}
      {error && <span className="error-text">{error}</span>}
      <button type="button" className="primary-btn danger-btn" onClick={handleDelete} disabled={isDeleting}>
        {t('userSettings.deleteAccountButton')}
      </button>
    </div>
  );
}

function UserSettings() {
  const { username, email, avatarUrl, changeCredentials, setAvatar } = useAuth();
  const { addToast } = useToast();
  const { t } = useTranslation('settings');
  const [newUsername, setNewUsername] = useState(username);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const initials = username ? username.slice(0, 2).toUpperCase() : '??';

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      addToast(t('userSettings.chooseImageFile'), 'error');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      addToast(t('userSettings.imageTooLarge'), 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const result = await setAvatar(reader.result);
      if (result.success) addToast(t('userSettings.pictureUpdated'), 'success');
      else addToast(result.error || t('userSettings.pictureUpdateFailed'), 'error');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = async () => {
    const result = await setAvatar(null);
    if (result.success) addToast(t('userSettings.pictureRemoved'), 'success');
    else addToast(result.error || t('userSettings.pictureRemoveFailed'), 'error');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!currentPassword) {
      setError(t('userSettings.currentPasswordRequired'));
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      setError(t('userSettings.passwordMismatch'));
      return;
    }

    const updates = {};
    if (newUsername.trim() && newUsername.trim() !== username) updates.username = newUsername.trim();
    if (newPassword) updates.password = newPassword;

    if (Object.keys(updates).length === 0) {
      addToast(t('userSettings.nothingToUpdate'), 'error');
      return;
    }

    const result = await changeCredentials(currentPassword, updates);
    if (!result.success) {
      setError(result.error);
      return;
    }

    addToast(t('userSettings.settingsUpdated'), 'success');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <section className="page-card">
      <div className="page-header">
        <div>
          <p className="eyebrow">{t('userSettings.eyebrow')}</p>
          <h2>{t('userSettings.heading')}</h2>
        </div>
      </div>

      <div className="card avatar-card">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="profile-avatar profile-avatar-img profile-avatar-large" />
        ) : (
          <span className="profile-avatar profile-avatar-large">{initials}</span>
        )}
        <div className="avatar-card-actions">
          <label className="text-btn avatar-upload-btn">
            {t('userSettings.uploadPicture')}
            <input type="file" accept="image/*" onChange={handleAvatarChange} hidden />
          </label>
          {avatarUrl && <button type="button" className="text-btn danger" onClick={handleRemoveAvatar}>{t('userSettings.remove')}</button>}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card form-card settings-form">
        <label>
          {t('userSettings.email')}
          <input value={email} disabled />
        </label>
        <label>
          {t('userSettings.username')}
          <input value={newUsername} onChange={(event) => setNewUsername(event.target.value)} />
        </label>
        <label>
          {t('userSettings.currentPassword')}
          <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
        </label>
        <label>
          {t('userSettings.newPassword')}
          <input type="password" placeholder={t('userSettings.newPasswordPlaceholder')} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
        </label>
        <label>
          {t('userSettings.confirmNewPassword')}
          <input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
        </label>
        {error && <span className="error-text">{error}</span>}
        <button className="primary-btn standout-btn" type="submit">{t('userSettings.saveChanges')}</button>
      </form>

      <SubscriptionBillingCard />
      <DangerZoneCard />
    </section>
  );
}

export default UserSettings;
